import { createRequestSender, RequestSender } from '@bigcommerce/request-sender';
import { createScriptLoader } from '@bigcommerce/script-loader';

import { PaymentIntegrationService } from '@bigcommerce/checkout-sdk/payment-integration-api';

import { BillingAddressActionCreator, BillingAddressRequestSender } from '../billing';
import { CartRequestSender } from '../cart';
import {
    CheckoutActionCreator,
    CheckoutRequestSender,
    CheckoutStore,
    CheckoutValidator,
} from '../checkout';
import { ConfigActionCreator, ConfigRequestSender } from '../config';
import CouponActionCreator from '../coupon/coupon-action-creator';
import CouponRequestSender from '../coupon/coupon-request-sender';
import { CustomerActionCreator, CustomerRequestSender } from '../customer';
import { FormFieldsActionCreator, FormFieldsRequestSender } from '../form';
import { HostedFormFactory } from '../hosted-form';
import { OrderActionCreator, OrderRequestSender } from '../order';
import {
    createPaymentClient,
    PaymentActionCreator,
    PaymentMethodActionCreator,
    PaymentMethodRequestSender,
    PaymentRequestSender,
    PaymentRequestTransformer,
} from '../payment';
import { PaymentProviderCustomerActionCreator } from '../payment-provider-customer';
import PaymentStrategyWidgetActionCreator from '../payment/payment-strategy-widget-action-creator';
import { RemoteCheckoutActionCreator, RemoteCheckoutRequestSender } from '../remote-checkout';
import {
    ConsignmentActionCreator,
    ConsignmentRequestSender,
    ShippingCountryActionCreator,
    ShippingCountryRequestSender,
} from '../shipping';
import {
    createSpamProtection,
    PaymentHumanVerificationHandler,
    SpamProtectionActionCreator,
    SpamProtectionRequestSender,
} from '../spam-protection';
import { StoreCreditActionCreator, StoreCreditRequestSender } from '../store-credit';
import { SubscriptionsActionCreator, SubscriptionsRequestSender } from '../subscription';

import createPaymentIntegrationSelectors from './create-payment-integration-selectors';
import DefaultPaymentIntegrationService from './default-payment-integration-service';
import PaymentIntegrationStoreProjectionFactory from './payment-integration-store-projection-factory';

export default function createPaymentIntegrationService(
    store: CheckoutStore,
    requestSender?: RequestSender,
): PaymentIntegrationService {
    const {
        config: { getHost, getLocale },
    } = store.getState();

    if (!requestSender) {
        requestSender = createRequestSender({ host: getHost() });
    }

    const storeProjectionFactory = new PaymentIntegrationStoreProjectionFactory(
        createPaymentIntegrationSelectors,
    );

    const checkoutActionCreator = new CheckoutActionCreator(
        new CheckoutRequestSender(requestSender),
        new ConfigActionCreator(new ConfigRequestSender(requestSender)),
        new FormFieldsActionCreator(new FormFieldsRequestSender(requestSender)),
    );

    const checkoutValidator = new CheckoutValidator(new CheckoutRequestSender(requestSender));

    const hostedFormFactory = new HostedFormFactory(store);

    const orderActionCreator = new OrderActionCreator(
        new OrderRequestSender(requestSender),
        new CheckoutValidator(new CheckoutRequestSender(requestSender)),
    );

    const billingAddressActionCreator = new BillingAddressActionCreator(
        new BillingAddressRequestSender(requestSender),
        new SubscriptionsActionCreator(new SubscriptionsRequestSender(requestSender)),
    );

    const consignmentActionCreator = new ConsignmentActionCreator(
        new ConsignmentRequestSender(requestSender),
        new CheckoutRequestSender(requestSender),
    );

    const paymentMethodActionCreator = new PaymentMethodActionCreator(
        new PaymentMethodRequestSender(requestSender),
    );

    const paymentActionCreator = new PaymentActionCreator(
        new PaymentRequestSender(createPaymentClient(store)),
        orderActionCreator,
        new PaymentRequestTransformer(),
        new PaymentHumanVerificationHandler(createSpamProtection(createScriptLoader())),
    );

    const paymentHumanVerificationHandler = new PaymentHumanVerificationHandler(
        createSpamProtection(createScriptLoader()),
    );

    const customerActionCreator = new CustomerActionCreator(
        new CustomerRequestSender(requestSender),
        checkoutActionCreator,
        new SpamProtectionActionCreator(
            createSpamProtection(createScriptLoader()),
            new SpamProtectionRequestSender(requestSender),
        ),
    );

    const storeCreditActionCreator = new StoreCreditActionCreator(
        new StoreCreditRequestSender(requestSender),
    );

    const applyCouponActionCreator = new CouponActionCreator(
        new CouponRequestSender(requestSender),
    );

    const spamProtection = createSpamProtection(createScriptLoader());
    const spamProtectionRequestSender = new SpamProtectionRequestSender(requestSender);
    const spamProtectionActionCreator = new SpamProtectionActionCreator(
        spamProtection,
        spamProtectionRequestSender,
    );

    const cartRequestSender = new CartRequestSender(requestSender);

    const paymentProviderCustomerActionCreator = new PaymentProviderCustomerActionCreator();

    const shippingCountryActionCreator = new ShippingCountryActionCreator(
        new ShippingCountryRequestSender(requestSender, { locale: getLocale() }),
        store,
    );

    const remoteCheckoutActionCreator = new RemoteCheckoutActionCreator(
        new RemoteCheckoutRequestSender(requestSender),
        checkoutActionCreator,
    );

    const paymentStrategyWidgetActionCreator = new PaymentStrategyWidgetActionCreator();

    return new DefaultPaymentIntegrationService(
        store,
        storeProjectionFactory,
        checkoutActionCreator,
        checkoutValidator,
        hostedFormFactory,
        orderActionCreator,
        billingAddressActionCreator,
        consignmentActionCreator,
        paymentMethodActionCreator,
        paymentActionCreator,
        paymentHumanVerificationHandler,
        customerActionCreator,
        cartRequestSender,
        storeCreditActionCreator,
        applyCouponActionCreator,
        spamProtectionActionCreator,
        paymentProviderCustomerActionCreator,
        shippingCountryActionCreator,
        remoteCheckoutActionCreator,
        paymentStrategyWidgetActionCreator,
    );
}
